/**
 * GORA — the site-wide behaviours.
 *
 * Everything here used to live in gora-journey.js, which is loaded by the
 * journey section, which exists only on the homepage. So on every other page
 * the reveals never fired — and .gora-reveal starts at opacity: 0 — which hid
 * three whole sections of the product page with no error anywhere. Behaviour
 * the whole site depends on has to be loaded by the whole site.
 *
 *   <gora-gallery>  product shots: thumbnails swap the main image
 *   gora-reveal     one-shot entry reveals, staggered by DOM order
 *   img[data-src]   deferred images, hydrated on a 600px approach
 *   --gora-header   the real measured height of Horizon's header
 *
 * The first thing it does is flag the document, so gora.css only ever hides
 * a reveal when this file is actually running.
 */

document.documentElement.dataset.goraJs = '';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* -------------------------------------------------------------------------
   The product gallery

   Deliberately small: thumbnails swap which shot is shown, arrow keys move
   between them, and that is all. A zoom or a lightbox on a bag of coffee is a
   feature nobody asked for and one more thing to keep working.
   ------------------------------------------------------------------------- */

class GoraGallery extends HTMLElement {
  connectedCallback() {
    this.shots = [...this.querySelectorAll('[data-shot]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    if (this.thumbs.length < 2) return;

    this.index = 0;
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.show(i));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = (this.index + step + this.thumbs.length) % this.thumbs.length;
      event.preventDefault();
      this.show(next);
      this.thumbs[next].focus();
    });
  }

  show(index) {
    if (index === this.index) return;
    this.index = index;
    this.shots.forEach((shot, i) => shot.setAttribute('data-active', String(i === index)));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
  }
}

if (!customElements.get('gora-gallery')) {
  customElements.define('gora-gallery', GoraGallery);
}
/* -------------------------------------------------------------------------
   Variant pills

   Everything it needs is already in the markup: each pill carries its variant
   id, its formatted price and whether it is in stock, all rendered server-side
   by Liquid. This only moves the selection and repaints the price row, so the
   page is correct before it runs and stays correct if it never does — the
   first variant is selected in the HTML and the form posts a real id either
   way.
   ------------------------------------------------------------------------- */

class GoraVariants extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-variant-input]');
    this.pills = [...this.querySelectorAll('[data-variant]')];
    if (!this.input || this.pills.length < 2) return;

    const row = this.closest('form');
    this.was = row?.querySelector('[data-was]');
    this.now = row?.querySelector('[data-now]');
    this.addBtn = row?.querySelector('.gora-product__add');

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.select(pill));
    });

    // Arrow keys inside a radiogroup, as the role promises.
    this.addEventListener('keydown', (event) => {
      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!step) return;
      const from = this.pills.findIndex((p) => p.getAttribute('aria-checked') === 'true');
      const next = this.pills[(from + step + this.pills.length) % this.pills.length];
      event.preventDefault();
      this.select(next);
      next.focus();
    });
  }

  select(pill) {
    if (pill.disabled) return;
    this.pills.forEach((p) => p.setAttribute('aria-checked', String(p === pill)));
    this.input.value = pill.dataset.variant;

    if (this.now) this.now.textContent = pill.dataset.price;
    if (this.was) {
      const compare = pill.dataset.compare;
      this.was.textContent = compare || '';
      this.was.hidden = !compare;
    }
    if (this.addBtn) {
      const available = pill.dataset.available === 'true';
      this.addBtn.disabled = !available;
    }
  }
}

if (!customElements.get('gora-variants')) {
  customElements.define('gora-variants', GoraVariants);
}

/* -------------------------------------------------------------------------
   The review slider

   It was a bare overflow-x scroller. That works, but it reads as a scrollbar
   to drag rather than something to step through, and Gal called the reviews
   out twice as the part that matters. So: arrows that move exactly one card,
   pointer drag on desktop (a phone already swipes), a rail that reports where
   you are, and a live count.

   The native scroller stays underneath all of it — the controls are rendered
   hidden and only revealed once this element upgrades, so a page where the
   script fails still has a working, swipeable list of reviews rather than a
   row of arrows that do nothing.
   ------------------------------------------------------------------------- */

class GoraSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.cards = [...this.track.children];
    if (this.cards.length < 2) return;

    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.thumb = this.querySelector('[data-thumb-rail]');
    this.countEl = this.querySelector('[data-count]');

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    this.track.addEventListener(
      'scroll',
      () => {
        this.schedule();
        this.snapAfterIdle();
      },
      { passive: true }
    );
    window.addEventListener('resize', () => this.schedule(), { passive: true });

    this.armDrag();
    this.setAttribute('data-ready', '');
    this.sync();
  }

  /* One card plus one gap. Measured from the DOM rather than assumed, because
     the card width is a clamp() and the gap is a clamp() too. */
  get stride() {
    const a = this.cards[0].getBoundingClientRect();
    const b = this.cards[1].getBoundingClientRect();
    return Math.round(b.left - a.left) || Math.round(a.width);
  }

  /* Where the next arrow press should land. While an animation is in flight
     scrollLeft is a moving number, so a second press must count from where we
     are heading, not from where we happen to be. */
  get targetIndex() {
    if (this.pendingIndex != null) return this.pendingIndex;
    return Math.round(this.track.scrollLeft / this.stride);
  }

  step(direction) {
    const last = this.cards.length - 1;
    const index = Math.min(last, Math.max(0, this.targetIndex + direction));
    this.goTo(index);
  }

  /* Moving to a card.

     The first version of this fought CSS scroll snapping: native smooth
     scrolling and `scroll-snap-type: mandatory` cancel each other, measured on
     this very scroller — an instant scrollBy(396) landed on 396, the same call
     with behavior:'smooth' landed back on 0. That is why the CSS snap is off
     once this element upgrades and the snapping happens here instead.

     With nothing left to fight, plain scrollTo is the right tool: it runs on
     the compositor, so it stays smooth on a busy main thread, which a
     hand-rolled requestAnimationFrame tween does not — a tween is only ever as
     smooth as the frame rate it is handed.

     No "animating" flag is needed. A smooth scroll keeps firing scroll events
     the whole way, which keeps resetting the idle timer below, so the snap
     check only runs once motion has actually stopped — by which point we are
     already on a card and it does nothing. */
  goTo(index) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = Math.max(0, Math.min(index * this.stride, max));
    this.pendingIndex = index;
    this.track.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });

    // Repaint the readout on the click, not on the next animation frame. The
    // rAF coalescing below is right for a stream of scroll events and wrong
    // for a single deliberate press: on a busy main thread it left the counter
    // and the disabled arrows a beat behind the thing they describe.
    this.sync();

    // pendingIndex is normally cleared by snapAfterIdle, which rides scroll
    // events. A goTo that lands where we already are fires none, so clear it
    // on a timer too rather than leaving sync reading a stale target forever.
    clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => {
      this.pendingIndex = null;
      this.sync();
    }, 900);
  }

  /* A swipe, a trackpad flick or a drag leaves the scroller between two cards.
     Wait for it to actually stop — 140ms with no scroll event — then close the
     gap to the nearest card. Held off while a hand is on the scroller, so it
     never yanks the thing somebody is still dragging. */
  snapAfterIdle() {
    clearTimeout(this.idleTimer);
    if (this.hasAttribute('data-dragging')) return;
    this.idleTimer = setTimeout(() => {
      clearTimeout(this.pendingTimer);
      this.pendingIndex = null;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const nearest = Math.round(this.track.scrollLeft / this.stride);
      const drift = Math.abs(nearest * this.stride - this.track.scrollLeft);
      // 2px of slack, and never at the very end, where the last card cannot
      // align without scrolling backwards away from it.
      if (drift > 2 && this.track.scrollLeft < max - 2) this.goTo(nearest);
    }, 140);
  }

  /* Native scrolling — a swipe, a trackpad, a drag — fires far faster than
     anything needs to be redrawn. Coalesce to one read per frame. */
  schedule() {
    if (this.pending) return;
    this.pending = requestAnimationFrame(() => {
      this.pending = null;
      this.sync();
    });
  }

  sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = Math.max(1, scrollWidth - clientWidth);

    // While an arrow press is still animating, scrollLeft is the old number.
    // Report where the slider is heading, so the readout matches the press.
    const left =
      this.pendingIndex == null
        ? scrollLeft
        : Math.max(0, Math.min(this.pendingIndex * this.stride, max));

    // 2px of slack: a scroller rarely lands on an exact integer.
    const atStart = left <= 2;
    const atEnd = left >= max - 2;

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    if (this.thumb) {
      const visible = Math.min(1, clientWidth / scrollWidth);
      this.thumb.style.width = `${visible * 100}%`;
      this.thumb.style.transform = `translateX(${(left / max) * (100 / visible - 100)}%)`;
    }

    if (this.countEl) {
      const index = Math.round(left / this.stride) + 1;
      this.countEl.textContent = `${Math.min(index, this.cards.length)} / ${this.cards.length}`;
    }
  }

  /* Grab-and-pan with a mouse. Touch is left to the browser, which already
     does it better than any of this would. */
  armDrag() {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = 0;

    const down = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('a, button')) return;
      // A hand on the scroller outranks an arrow press still animating: stop
      // the smooth scroll by pinning the scroller where it currently sits.
      this.track.scrollTo({ left: this.track.scrollLeft, behavior: 'auto' });
      clearTimeout(this.pendingTimer);
      this.pendingIndex = null;
      dragging = true;
      moved = 0;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.setAttribute('data-dragging', '');
    };

    const move = (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      moved = Math.max(moved, Math.abs(delta));
      this.track.scrollLeft = startScroll - delta;
      event.preventDefault();
    };

    const up = (event) => {
      if (!dragging) return;
      dragging = false;
      this.removeAttribute('data-dragging');
      this.snapAfterIdle();
      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch {
        /* the pointer was already released by the browser */
      }
      // A drag that crossed a card must not also fire that card's link.
      if (moved > 6) {
        const swallow = (click) => {
          click.preventDefault();
          click.stopPropagation();
        };
        this.track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 0);
      }
    };

    this.track.addEventListener('pointerdown', down);
    this.track.addEventListener('pointermove', move);
    this.track.addEventListener('pointerup', up);
    this.track.addEventListener('pointercancel', up);
  }
}

if (!customElements.get('gora-slider')) {
  customElements.define('gora-slider', GoraSlider);
}

/* -------------------------------------------------------------------------
   Entry reveals
   ------------------------------------------------------------------------- */

function initReveals(root = document) {
  const targets = root.querySelectorAll('.gora-reveal:not([data-shown])');
  if (!targets.length) return;

  if (REDUCED.matches) {
    targets.forEach((el) => el.setAttribute('data-shown', 'true'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-shown', 'true');
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
  );

  targets.forEach((el) => observer.observe(el));
}

/* -------------------------------------------------------------------------
   Deferred images

   Native loading="lazy" is generous: Chrome fetches anything within roughly a
   viewport or two, which pulled the whole review scroller down before the
   visitor had left the hero. Images marked with data-src wait for a 600px
   approach instead. <gora-journey> hydrates its own plates one chapter ahead
   and is skipped here, because its five plates are stacked and all "in view"
   at once. A <noscript> copy covers the case where none of this runs.
   ------------------------------------------------------------------------- */

function initLazyImages(root = document) {
  const targets = [...root.querySelectorAll('img[data-src]')].filter(
    (img) => !img.closest('gora-journey')
  );
  if (!targets.length) return;

  const hydrate = (img) => {
    // Sources first, or the browser has already committed to the <img> src by
    // the time a <picture>'s better candidate arrives.
    const parent = img.parentElement;
    if (parent && parent.tagName === 'PICTURE') {
      parent.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      });
    }
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  };

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        hydrate(entry.target);
      });
    },
    // 600px on all four sides, not just top and bottom: the review slider
    // scrolls sideways, and a horizontal-only approach left the next card's
    // photo as a grey box until it was already on screen.
    { rootMargin: '600px', threshold: 0 }
  );

  targets.forEach((img) => observer.observe(img));
}

/* -------------------------------------------------------------------------
   Horizon's header height

   Every sticky offset in gora.css clears the theme header through
   --gora-header. That token used to be a hardcoded 5rem, which is a guess:
   Horizon's bar is taller on a phone, so a slot opened between the header and
   the pinned altitude readout and chapter text scrolled through it. Measure
   the real element instead of guessing at it.
   ------------------------------------------------------------------------- */

function trackHeaderHeight() {
  const header = document.getElementById('header-component');
  if (!header) return;

  const apply = () => {
    const height = Math.round(header.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--gora-header', `${height}px`);
    }
  };

  apply();
  if ('ResizeObserver' in window) new ResizeObserver(apply).observe(header);
  window.addEventListener('resize', apply, { passive: true });
}

function init(root) {
  initReveals(root);
  initLazyImages(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    trackHeaderHeight();
    init();
  });
} else {
  trackHeaderHeight();
  init();
}

// The theme editor swaps section markup in place; re-arm whatever arrives.
document.addEventListener('shopify:section:load', (event) => init(event.target));
